import React from 'react';
import { motion } from 'framer-motion';
import { SPECTRUM_MAX, TARGET_MAX, TARGET_MIN } from '../utils/responseModel';
type Marker = {
  n: number;
  value: string;
};
type ResponseContinuumProps = {
  user: Marker | null;
  indiv: Marker | null;
  ceiling: number | null;
  reduced: boolean;
};
const LOW_EDGE = TARGET_MIN - 0.5;
const HIGH_EDGE = TARGET_MAX + 0.5;
const TICKS = Array.from({
  length: SPECTRUM_MAX + 1
}, (_, i) => i);
const SPRING = {
  type: 'spring',
  stiffness: 170,
  damping: 22
} as const;
const pct = (v: number) => Math.min(SPECTRUM_MAX, Math.max(0, v)) / SPECTRUM_MAX * 100;
const ZONES = [{
  label: 'Under-response',
  from: 0,
  to: LOW_EDGE,
  tint: 'bg-under-tint',
  text: 'text-under'
}, {
  label: 'Target response',
  from: LOW_EDGE,
  to: HIGH_EDGE,
  tint: 'bg-target-tint',
  text: 'text-target'
}, {
  label: 'Over-response',
  from: HIGH_EDGE,
  to: SPECTRUM_MAX,
  tint: 'bg-over-tint',
  text: 'text-over'
}];
export function ResponseContinuum({
  user,
  indiv,
  ceiling,
  reduced
}: ResponseContinuumProps) {
  const showCeiling = ceiling !== null && ceiling <= SPECTRUM_MAX;
  return <figure className="rounded-xl border border-rule bg-white" data-id="element-222" data-mp="25zwvpe8wu2r2">
      <figcaption className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule px-5 py-3 md:px-6" data-id="element-223" data-mp="2113i7ltmibqw">
        <span className="font-serif text-lg text-ink" data-id="element-224" data-mp="1x001yy7nydp5">Ovarian response</span>
        <span className="font-mono text-[11px] text-graphite/70" data-id="element-225" data-mp="2tpr85upbhpas">Illustrative response model · not a clinical prediction</span>
      </figcaption>

      <div className="px-5 pb-5 pt-4 md:px-8" data-id="element-226" data-mp="2orz647uykmsv">
        <div className="relative h-6" data-id="element-227" data-mp="2ot0komb6nrfc">
          {ZONES.map(z => <span key={z.label} className={`absolute top-0 -translate-x-1/2 whitespace-nowrap text-xs font-medium md:text-[13px] ${z.text}`} style={{
          left: `${(pct(z.from) + pct(z.to)) / 2}%`
        }} data-id="element-228" data-mp="2jy4qbfih4rz2">
              <span className="hidden sm:inline" data-id="element-229" data-mp="37p0bvuwc6qxv">{z.label}</span>
              <span className="sm:hidden" data-id="element-230" data-mp="37np99yemnp8c">{z.label.split('-')[0].split(' ')[0]}</span>
            </span>)}
        </div>

        <div className="relative h-40 overflow-hidden rounded-md" data-id="element-231" data-mp="1armb5q06set1">
          {ZONES.map(z => <div key={z.label} className={`absolute inset-y-0 ${z.tint}`} style={{
          left: `${pct(z.from)}%`,
          width: `${pct(z.to) - pct(z.from)}%`
        }} data-id="element-232" data-mp="w2q4nxds39uj" />)}
          <div className="absolute inset-y-0 border-x border-target/40" style={{
          left: `${pct(LOW_EDGE)}%`,
          width: `${pct(HIGH_EDGE) - pct(LOW_EDGE)}%`
        }} data-id="element-233" data-mp="n0gvbau8xmiq" />

          {showCeiling && ceiling !== null && <div className="absolute inset-y-0 border-l border-dashed border-ink/40" style={{
          left: `${pct(ceiling)}%`
        }} data-id="element-234" data-mp="d5f7qjfmcsi9">
              <span className="absolute bottom-2 left-1.5 whitespace-nowrap font-mono text-[10px] tracking-[0.1em] text-graphite" data-id="element-235" data-mp="27hbzkdhz40du">
                RESERVE CEILING
              </span>
            </div>}

          {user === null && <p className="absolute inset-0 flex items-center justify-center text-sm text-graphite" data-id="element-236" data-mp="tuytogaba16">
              Run the response to place your current choice
            </p>}

          {user && indiv && <Arrow from={pct(user.n)} to={pct(indiv.n)} reduced={reduced} data-id="element-237" data-mp="akh68vduku8s" />}

          {user && <motion.div className="absolute inset-y-0 w-0" initial={{
          left: reduced ? `${pct(user.n)}%` : '0%'
        }} animate={{
          left: `${pct(user.n)}%`
        }} transition={reduced ? {
          duration: 0
        } : SPRING} data-id="element-238" data-mp="5qlck54m9tjf">
              <span className="absolute inset-y-0 -left-px w-[2px] bg-ink" data-id="element-239" data-mp="ti0ie7vlqszf" />
              <span className="absolute -left-[6px] top-0 h-3 w-3 rounded-full border-2 border-white bg-ink" data-id="element-240" data-mp="oixbsizz4epc" />
              <MarkerLabel side={pct(user.n) > 62 ? 'left' : 'right'} top="top-3" title="Your current choice" value={user.value} tone="text-ink" data-id="element-241" data-mp="2v8lo7c4rkl0g" />
            </motion.div>}

          {user && indiv && <motion.div className="absolute inset-y-0 w-0" initial={{
          left: `${pct(user.n)}%`,
          opacity: 0
        }} animate={{
          left: `${pct(indiv.n)}%`,
          opacity: 1
        }} transition={reduced ? {
          duration: 0
        } : {
          ...SPRING,
          opacity: {
            duration: 0.15
          }
        }} data-id="element-242" data-mp="2rrzeu7331djo">
              <span className="absolute inset-y-0 -left-px w-[2px] bg-accent" data-id="element-243" data-mp="36n5l8v63bedj" />
              <span className="absolute -left-[6px] top-0 h-3 w-3 rounded-full border-2 border-white bg-accent" data-id="element-244" data-mp="291hog95q2u1h" />
              <MarkerLabel side={pct(indiv.n) > 62 ? 'left' : 'right'} top="top-[68px]" title="REKOVELLE individualized" value={indiv.value} tone="text-accent" data-id="element-245" data-mp="2e0kt3shsiy8x" />
            </motion.div>}
        </div>

        <div className="relative mt-1 h-2" aria-hidden="true" data-id="element-246" data-mp="1q9fe1fjoc9zo">
          {TICKS.map(v => <span key={v} className={`absolute top-0 w-px -translate-x-1/2 ${v % 4 === 0 ? 'h-2 bg-ink/50' : 'h-1 bg-ink/20'}`} style={{
          left: `${pct(v)}%`
        }} data-id="element-247" data-mp="1v80dhcr3qpn2" />)}
        </div>
        <div className="mt-1.5 flex justify-between text-[11px] text-graphite" aria-hidden="true" data-id="element-248" data-mp="8ar6ewcxehec">
          <span data-id="element-249" data-mp="3fbui9yhavkk">← Too little</span>
          <span data-id="element-250" data-mp="3ukld3gk587k5">Target window</span>
          <span data-id="element-251" data-mp="3ujacfplzvfx9">Too much →</span>
        </div>
      </div>
    </figure>;
}
function MarkerLabel({
  side,
  top,
  title,
  value,
  tone
}: {
  side: 'left' | 'right';
  top: string;
  title: string;
  value: string;
  tone: string;
}) {
  return <span className={`absolute ${top} ${side === 'right' ? 'left-3' : 'right-3 text-right'} whitespace-nowrap`} data-id="element-252" data-mp="3grvod4d8cojc">
      <span className={`block text-xs font-medium ${tone}`} data-id="element-253" data-mp="3bu3pfqe6425n">{title}</span>
      <span className={`block font-serif text-lg leading-tight tabular-nums sm:text-2xl ${tone}`} data-id="element-254" data-mp="36ty3mqukx9vh">{value}</span>
    </span>;
}
function Arrow({
  from,
  to,
  reduced
}: {
  from: number;
  to: number;
  reduced: boolean;
}) {
  if (Math.abs(to - from) < 2.5) return null;
  const left = Math.min(from, to);
  const width = Math.abs(to - from);
  const rightward = to > from;
  return <motion.div className="absolute bottom-8 h-px bg-accent" style={{
    left: `${left}%`,
    width: `${width}%`,
    transformOrigin: rightward ? 'left' : 'right'
  }} initial={{
    scaleX: reduced ? 1 : 0,
    opacity: 0
  }} animate={{
    scaleX: 1,
    opacity: 1
  }} transition={{
    duration: 0.28,
    delay: reduced ? 0 : 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} aria-hidden="true" data-id="element-255" data-mp="2x490273gqj2h">
      <span className={`absolute top-1/2 h-0 w-0 -translate-y-1/2 border-y-[4px] border-y-transparent ${rightward ? 'right-0 border-l-[7px] border-l-accent' : 'left-0 border-r-[7px] border-r-accent'}`} data-id="element-256" data-mp="3fz7gzdffd04c" />
    </motion.div>;
}